import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import './Hero.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import WhatsAppFloat from '../common/WhatsAppFloat';
import { searchPlaces, getPlaceDetails } from '../../services/maps';
import { createOneWayQuote } from '../../services/quotes';
import { saveQuote } from '../../utils/session';

const DEFAULT_LOCATIONS = [
  {
    id: 1,
    name: 'Patna Airport',
    address: 'Ashiana More Rd, Khajpura, Patna, Bihar 800014, India',
    coordinates: '85.0844413,25.5994213'
  },
  {
    id: 2,
    name: 'Patna Junction',
    address: 'Station Rd, Near Mahavir Mandir, Patna, Bihar 800001, India',
    coordinates: '85.1376,25.5941'
  },
  {
    id: 3,
    name: 'Danapur Junction',
    address: 'Danapur, Patna, Bihar 801503, India',
    coordinates: '85.0444,25.6377'
  },
  {
    id: 4,
    name: 'Gandhi Maidan',
    address: 'Gandhi Maidan, Patna, Bihar 800001, India',
    coordinates: '85.1376,25.5941'
  },
  {
    id: 5,
    name: 'Darbhanga Airport',
    address: 'Allalpatti, Darbhanga, Bihar 846005, India',
    coordinates: '85.9137,26.1707'
  },
  { id: 999, name: 'Other', address: '', coordinates: '' }
];

const VEHICLE_TYPES = {
  mini: { basePrice: 20, name: 'Mini' },
  sedan: { basePrice: 27, name: 'Sedan' },
  suv: { basePrice: 34, name: 'SUV' },
  luxury: { basePrice: 41, name: 'Luxury' },
  van: { basePrice: 48, name: 'Van' }
};

const VEHICLE_ICONS = {
  mini: '\uD83D\uDE97',
  sedan: '\uD83D\uDE98',
  suv: '\uD83D\uDE99',
  luxury: '\uD83C\uDFCE\uFE0F',
  van: '\uD83D\uDE90'
};

const Hero = () => {
  const [bookingData, setBookingData] = useState({
    pickupLocation: '',
    dropLocation: '',
    dateTime: '',
    passengers: '1',
    vehicleType: 'mini',
    mobileNumber: '',
    email: ''
  });

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [pickupNoResult, setPickupNoResult] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [dropoffNoResult, setDropoffNoResult] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [modalType, setModalType] = useState('pickup');
  const [customLocation, setCustomLocation] = useState({ full: '', error: '' });
  const pickupTimeout = useRef();
  const dropoffTimeout = useRef();
  const navigate = useNavigate();
  const [showPickupOther, setShowPickupOther] = useState(false);
  const [showDropoffOther, setShowDropoffOther] = useState(false);
  const [pickupOtherValue, setPickupOtherValue] = useState('');
  const [dropoffOtherValue, setDropoffOtherValue] = useState('');
  const [pickupDropdownHover, setPickupDropdownHover] = useState(false);
  const [dropoffDropdownHover, setDropoffDropdownHover] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [otherPlaceholder, setOtherPlaceholder] = useState("Village/Area, City/Zilla, Pincode");
  const [selectedDate, setSelectedDate] = useState(null);
  const [formError, setFormError] = useState('');
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  // Prefill form if coming back from Estimate page
  useEffect(() => {
    if (location.state) {
      setBookingData(prev => ({ ...prev, ...location.state }));
      // Optionally set other states like selectedDate, pickupOtherValue, etc. if present
      if (location.state.selectedDate) setSelectedDate(location.state.selectedDate);
      if (location.state.pickupOtherValue) setPickupOtherValue(location.state.pickupOtherValue);
      if (location.state.dropoffOtherValue) setDropoffOtherValue(location.state.dropoffOtherValue);
      if (location.state.showPickupOther) setShowPickupOther(location.state.showPickupOther);
      if (location.state.showDropoffOther) setShowDropoffOther(location.state.showDropoffOther);
    }
  }, [location.state]);

  // Update the debounced fetch for pickup
  useEffect(() => {
    if (showPickupSuggestions) {
      if (pickupTimeout.current) clearTimeout(pickupTimeout.current);
      pickupTimeout.current = setTimeout(async () => {
        if (bookingData.pickupLocation.trim() === '') {
          setPickupSuggestions(DEFAULT_LOCATIONS);
          setPickupNoResult(false);
          return;
        }
        const results = await searchPlaces(bookingData.pickupLocation);
        setPickupSuggestions([...results, { id: 999, name: 'Other', address: '' }]);
        setPickupNoResult(results.length === 0);
      }, 400);
    }
    return () => clearTimeout(pickupTimeout.current);
  }, [bookingData.pickupLocation, showPickupSuggestions]);

  // Update the debounced fetch for dropoff
  useEffect(() => {
    if (showDropoffSuggestions) {
      if (dropoffTimeout.current) clearTimeout(dropoffTimeout.current);
      dropoffTimeout.current = setTimeout(async () => {
        if (bookingData.dropLocation.trim() === '') {
          setDropoffSuggestions(DEFAULT_LOCATIONS);
          setDropoffNoResult(false);
          return;
        }
        const results = await searchPlaces(bookingData.dropLocation);
        setDropoffSuggestions([...results, { id: 999, name: 'Other', address: '' }]);
        setDropoffNoResult(results.length === 0);
      }, 400);
    }
    return () => clearTimeout(dropoffTimeout.current);
  }, [bookingData.dropLocation, showDropoffSuggestions]);

  useEffect(() => {
    const options = [
      "Village/Area, City/Zilla, Pincode",
      "e.g. Maner, Patna, 801108"
    ];
    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % options.length;
      setOtherPlaceholder(options[idx]);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Show default suggestions on focus
  const handleFocus = (type) => {
    if (type === 'pickup') {
      setShowPickupSuggestions(true);
      setPickupSuggestions(DEFAULT_LOCATIONS);
      setPickupNoResult(false);
    } else {
      setShowDropoffSuggestions(true);
      setDropoffSuggestions(DEFAULT_LOCATIONS);
      setDropoffNoResult(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Prevent backend search if 'Other' is selected
    if ((name === 'pickupLocation' && showPickupOther) || (name === 'dropLocation' && showDropoffOther)) {
      return;
    }
    setBookingData(prev => ({ ...prev, [name]: value }));
    if (name === 'pickupLocation') {
      setShowPickupSuggestions(true);
      setPickupPlace(null);
    }
    if (name === 'dropLocation') {
      setShowDropoffSuggestions(true);
      setDropoffPlace(null);
    }
  };

  // Update handleSuggestionClick to use toast notifications
  const handleSuggestionClick = async (location, type) => {
    if (location.name === 'Other') {
      if (type === 'pickupLocation') {
        setShowPickupOther(true);
        setBookingData(prev => ({ ...prev, pickupLocation: 'Other' }));
      } else {
        setShowDropoffOther(true);
        setBookingData(prev => ({ ...prev, dropLocation: 'Other' }));
      }
      return;
    }

    try {
      let coords;
      let lat;
      let lng;
      let placeId = null;
      if (location.coordinates) {
        // Default locations store coords as "lng,lat"
        coords = location.coordinates;
        const [lngStr, latStr] = location.coordinates.split(',');
        lng = Number(lngStr);
        lat = Number(latStr);
      } else {
        // For searched locations, get coordinates from Places API
        placeId = location.placeId || location.id;
        const placeDetails = await getPlaceDetails(placeId);
        if (!placeDetails) {
          toast.error('Failed to get location details. Please try again.');
          return;
        }
        lat = placeDetails.location.lat;
        lng = placeDetails.location.lng;
        coords = `${lng},${lat}`;
      }

      const resolved = {
        address: location.address || location.name,
        displayName: location.name,
        lat,
        lng,
        placeId,
      };

      setBookingData(prev => ({
        ...prev,
        [type]: location.name,
        [`${type}Coordinates`]: coords
      }));

      if (type === 'pickupLocation') {
        setPickupPlace(resolved);
        setShowPickupSuggestions(false);
        setShowPickupOther(false);
        setPickupOtherValue('');
      } else {
        setDropoffPlace(resolved);
        setShowDropoffSuggestions(false);
        setShowDropoffOther(false);
        setDropoffOtherValue('');
      }
    } catch (error) {
      console.error('Error handling suggestion click:', error);
      if (error.message === 'LOCATION_OUTSIDE_BIHAR') {
        toast.error('We currently only serve within Bihar.');
      } else {
        toast.error('Error processing location. Please try again.');
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleCustomInput = (e) => {
    setCustomLocation({ full: e.target.value, error: '' });
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    // Validate format: must contain at least two arrows and a 6-digit pincode at the end
    const parts = customLocation.full.split('->').map(s => s.trim());
    const pincode = parts[parts.length - 1];
    if (parts.length < 3 || !/^[1-9][0-9]{5}$/.test(pincode)) {
      setCustomLocation(prev => ({ ...prev, error: 'Please enter in format: Village name -> Zilla Name -> Pincode (6 digits)' }));
      return;
    }
    if (modalType === 'pickup') {
      setBookingData(prev => ({ ...prev, pickupLocation: customLocation.full }));
      setShowPickupSuggestions(false);
    } else {
      setBookingData(prev => ({ ...prev, dropLocation: customLocation.full }));
      setShowDropoffSuggestions(false);
    }
    setShowModal(false);
  };

  const handleViewEstimate = async (e) => {
    e.preventDefault();
    if (!(showPickupOther ? pickupOtherValue : bookingData.pickupLocation)) {
      setFormError('Pickup location is required.');
      return;
    }
    if (!(showDropoffOther ? dropoffOtherValue : bookingData.dropLocation)) {
      setFormError('Dropoff location is required.');
      return;
    }
    if (!selectedDate) {
      setFormError('Date and time are required.');
      return;
    }
    if (!bookingData.vehicleType) {
      setFormError('Vehicle type is required.');
      return;
    }
    if (!/^[6-9]\d{9}$/.test((bookingData.mobileNumber || '').trim())) {
      setFormError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (!pickupPlace || !dropoffPlace) {
      setFormError('Please pick pickup & drop from the suggestions list so we can compute your fare.');
      return;
    }
    if (pickupPlace.placeId && dropoffPlace.placeId && pickupPlace.placeId === dropoffPlace.placeId) {
      setFormError('Pickup and drop cannot be the same location.');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      const travelDate = format(selectedDate, 'yyyy-MM-dd');
      const pickupTime = format(selectedDate, 'HH:mm');
      const quote = await createOneWayQuote({
        origin: {
          lat: pickupPlace.lat,
          lng: pickupPlace.lng,
          address: pickupPlace.address,
          placeId: pickupPlace.placeId || undefined,
        },
        destination: {
          lat: dropoffPlace.lat,
          lng: dropoffPlace.lng,
          address: dropoffPlace.address,
          placeId: dropoffPlace.placeId || undefined,
        },
        vehicleId: bookingData.vehicleType,
        travelDate,
        pickupTime,
      });
      if (!quote?.success && !quote?.quoteId) {
        toast.error(quote?.message || quote?.error || 'Could not generate fare. Please try again.');
        return;
      }
      saveQuote({
        ...quote,
        customer: {
          phone: bookingData.mobileNumber.trim(),
          email: (bookingData.email || '').trim() || null,
        },
        passengers: Number(bookingData.passengers) || 1,
      });
      navigate('/estimate');
    } catch (err) {
      const apiErr = err?.response?.data?.error;
      if (apiErr === 'NO_ROUTE_FOUND') setFormError('No road route found between these locations.');
      else if (apiErr === 'VEHICLE_NOT_FOUND') setFormError('Selected vehicle is not available on this route.');
      else if (apiErr === 'LOCATION_OUTSIDE_BIHAR') setFormError('We currently serve only pickups & drops inside Bihar.');
      else setFormError('Could not generate fare. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const formEl = document.querySelector('.hero-right');
    if (formEl) {
      formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="hero">
      {/* LEFT SIDE */}
      <div className="hero-left">
        <div className="hero-badge">
          <span className="badge-dot"></span>
          <span>Pan-Bihar &middot; 38 Districts &middot; 24/7</span>
        </div>

        <h1>
          Bihar ke har
          <span>zile mein.</span>
        </h1>

        <p className="hero-sub">
          One-way taxi, TaxiPool, tours, wedding vehicles, house shifting — honest prices, always. No hidden charges. Ever.
        </p>

        <div className="hero-btns">
          <button className="btn-or" onClick={scrollToForm} type="button">
            Book a ride
            <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
          <button className="btn-ghost" onClick={() => {
            const el = document.getElementById('services');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              navigate('/services');
            }
          }} type="button">
            Explore services
          </button>
        </div>

        <div className="hero-stats">
          <div className="stat-item">
            <span className="stat-num">38</span>
            <span className="stat-label">Districts covered</span>
          </div>
          <div className="stat-item">
            <span className="stat-num">{'\u20B9'}0</span>
            <span className="stat-label">Hidden charges</span>
          </div>
          <div className="stat-item">
            <span className="stat-num">24/7</span>
            <span className="stat-label">Availability</span>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="hero-right">
        <div className="booking-header">
          <h3>Book your cab</h3>
          <span>Confirm within 15 minutes</span>
        </div>

        <div className="booking-form">
          {/* Pickup & Drop */}
          <div className="form-row">
            <div className="fg">
              <label>Pickup city</label>
              <div className="location-input-container">
                <input
                  type="text"
                  name="pickupLocation"
                  placeholder="e.g. Patna"
                  value={showPickupOther ? 'Other' : bookingData.pickupLocation}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('pickup')}
                  onBlur={() => {
                    setTimeout(() => {
                      if (!pickupDropdownHover) setShowPickupSuggestions(false);
                    }, 100);
                  }}
                  autoComplete="off"
                  required={!showPickupOther}
                  disabled={showPickupOther}
                  readOnly={showPickupOther}
                />
                {showPickupSuggestions && !showPickupOther && (
                  <div
                    className="suggestions-list"
                    onMouseEnter={() => setPickupDropdownHover(true)}
                    onMouseLeave={() => setPickupDropdownHover(false)}
                  >
                    {pickupSuggestions.map(loc => (
                      <div
                        key={loc.id}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(loc, 'pickupLocation')}
                      >
                        <div className="suggestion-name">{loc.name}</div>
                        {loc.address && <div className="suggestion-address">{loc.address}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {showPickupOther && (
                  <input
                    type="text"
                    className="other-location-input sliding-placeholder"
                    style={{ marginTop: '6px' }}
                    placeholder="Area, City, Pin (e.g. Maner, Patna, 801108)"
                    value={pickupOtherValue}
                    onChange={e => setPickupOtherValue(e.target.value)}
                    required
                    autoFocus
                  />
                )}
              </div>
            </div>
            <div className="fg">
              <label>Drop city</label>
              <div className="location-input-container">
                <input
                  type="text"
                  name="dropLocation"
                  placeholder="e.g. Gaya"
                  value={showDropoffOther ? 'Other' : bookingData.dropLocation}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('dropoff')}
                  onBlur={() => {
                    setTimeout(() => {
                      if (!dropoffDropdownHover) setShowDropoffSuggestions(false);
                    }, 100);
                  }}
                  autoComplete="off"
                  required={!showDropoffOther}
                  disabled={showDropoffOther}
                  readOnly={showDropoffOther}
                />
                {showDropoffSuggestions && !showDropoffOther && (
                  <div
                    className="suggestions-list"
                    onMouseEnter={() => setDropoffDropdownHover(true)}
                    onMouseLeave={() => setDropoffDropdownHover(false)}
                  >
                    {dropoffSuggestions.map(loc => (
                      <div
                        key={loc.id}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(loc, 'dropLocation')}
                      >
                        <div className="suggestion-name">{loc.name}</div>
                        {loc.address && <div className="suggestion-address">{loc.address}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {showDropoffOther && (
                  <input
                    type="text"
                    className="other-location-input sliding-placeholder"
                    style={{ marginTop: '6px' }}
                    placeholder="Area, City, Pin (e.g. Maner, Patna, 801108)"
                    value={dropoffOtherValue}
                    onChange={e => setDropoffOtherValue(e.target.value)}
                    required
                    autoFocus
                  />
                )}
              </div>
            </div>
          </div>

          {/* Date & Time */}
          {(() => {
            const now = new Date();
            const isToday = selectedDate &&
              new Date(selectedDate).toDateString() === now.toDateString();
            const minTime = isToday ? now : new Date(new Date().setHours(0, 0, 0, 0));
            const maxTime = new Date(new Date().setHours(23, 59, 0, 0));
            return (
              <div className="form-row">
                <div className="fg">
                  <label>Travel date</label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      selected={selectedDate}
                      onChange={date => {
                        if (date) {
                          const nowDt = new Date();
                          const isTodayPick = date.toDateString() === nowDt.toDateString();
                          if (isTodayPick && date < nowDt) {
                            date.setHours(nowDt.getHours(), nowDt.getMinutes(), 0, 0);
                          }
                        }
                        setSelectedDate(date);
                      }}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Pick date"
                      className="form-control"
                      required
                      minDate={new Date()}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="fg">
                  <label>Pickup time</label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      selected={selectedDate}
                      onChange={date => setSelectedDate(date)}
                      showTimeSelect
                      showTimeSelectOnly
                      timeFormat="hh:mm aa"
                      timeIntervals={15}
                      dateFormat="hh:mm aa"
                      placeholderText="Pick time"
                      className="form-control"
                      autoComplete="off"
                      minTime={minTime}
                      maxTime={maxTime}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Vehicle Type */}
          <div style={{ marginBottom: '4px' }}>
            <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(18,16,14,0.45)' }}>Vehicle type</label>
          </div>
          <div className="vehicle-row">
            {Object.entries(VEHICLE_TYPES).map(([key, { name, basePrice }]) => (
              <div
                key={key}
                className={`veh${bookingData.vehicleType === key ? ' active' : ''}`}
                onClick={() => setBookingData(prev => ({ ...prev, vehicleType: key }))}
              >
                <span className="veh-icon">{VEHICLE_ICONS[key]}</span>
                <span className="veh-name">{name}</span>
              </div>
            ))}
          </div>

          {/* Mobile Number + Email */}
          <div className="form-row" style={{ marginBottom: '14px' }}>
            <div className="fg">
              <label>Mobile number</label>
              <input
                type="tel"
                name="mobileNumber"
                placeholder="10-digit mobile"
                value={bookingData.mobileNumber}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="fg">
              <label>Email <span style={{ color: 'rgba(18,16,14,0.45)', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                value={bookingData.email}
                onChange={handleInputChange}
              />
            </div>
          </div>


          {/* Error */}
          {formError && <div className="form-error-msg">{formError}</div>}

          {/* Submit */}
          <button
            type="button"
            className="submit-btn"
            onClick={handleViewEstimate}
            disabled={submitting}
          >
            {submitting ? 'Getting fare…' : 'View Estimated Fare →'}
          </button>

          {/* Explore other services */}
          <p className="explore-services-link">
            Looking for tours, weddings or shifting?{' '}
            <Link to="/services">&rarr; Explore all services</Link>
          </p>

          {/* Trust Row */}
          <div className="trust-row">
            <div className="trust-item">
              <span className="trust-dot"></span>
              No hidden charges
            </div>
            <div className="trust-item">
              <span className="trust-dot"></span>
              Verified drivers
            </div>
            <div className="trust-item">
              <span className="trust-dot"></span>
              24/7 support
            </div>
            <div className="trust-item">
              <span className="trust-dot"></span>
              Free cancellation
            </div>
          </div>
        </div>
      </div>

      {/* Modal for custom location */}
      {showModal && (
        <div className="custom-modal-overlay" onClick={closeModal}>
          <div className="custom-modal" onClick={e => e.stopPropagation()}>
            <h3>Enter Full Address</h3>
            <form onSubmit={handleCustomSubmit}>
              <input
                type="text"
                name="full"
                placeholder="Village name -> Zilla Name -> Pincode"
                value={customLocation.full}
                onChange={handleCustomInput}
                required
              />
              <div className="modal-tip">Please enter in format: <b>Village name &gt; Zilla Name &gt; Pincode</b></div>
              {customLocation.error && <div className="error-msg">{customLocation.error}</div>}
              <div className="modal-actions">
                <button type="button" className="modal-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="modal-submit" disabled={!customLocation.full}>Use This Address</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <WhatsAppFloat />
    </section>
  );
};

export default Hero;
